import { useState, useEffect } from "react";
import { 
  Bell, 
  Mail, 
  CheckCircle, 
  XCircle,
  Save,
  Send,
  RefreshCw,
  Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationSettings = ({ token }) => {
  const [settings, setSettings] = useState({
    new_quote_email: true,
    booking_confirmation_email: true,
    visit_reminder_email: true,
    review_request_email: false,
    owner_notification_email: "",
    sendgrid_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/owner/notification-settings`, { headers });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching notification settings:", error);
      toast.error("Failed to load notification settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/owner/notification-settings`, {
        new_quote_email: settings.new_quote_email,
        booking_confirmation_email: settings.booking_confirmation_email,
        visit_reminder_email: settings.visit_reminder_email,
        review_request_email: settings.review_request_email,
        owner_notification_email: settings.owner_notification_email
      }, { headers });
      toast.success("Notification settings saved");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await axios.post(`${API}/notifications/send-test`, {}, { headers });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      console.error("Error sending test email:", error);
      toast.error("Failed to send test email");
    } finally {
      setTestingEmail(false);
    }
  };

  const notificationTypes = [
    {
      key: "new_quote_email",
      title: "New Quote Notification",
      description: "Send email to owner when a new quote request is submitted",
      icon: Mail
    },
    {
      key: "booking_confirmation_email",
      title: "Booking Confirmation",
      description: "Send confirmation email to customer after booking a site visit",
      icon: CheckCircle
    },
    {
      key: "visit_reminder_email",
      title: "Visit Reminder",
      description: "Send reminder email 24 hours before scheduled site visit",
      icon: Bell
    },
    {
      key: "review_request_email",
      title: "Review Request",
      description: "Send review request email after job completion",
      icon: Mail
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Notification Settings</h2>
        <p className="text-gray-600">Configure automated email notifications</p>
      </div>

      {/* SendGrid Status */}
      <div className={`p-4 rounded-xl border ${settings.sendgrid_configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-start gap-3">
          {settings.sendgrid_configured ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
          ) : (
            <Info className="w-5 h-5 text-amber-600 mt-0.5" />
          )}
          <div>
            <h3 className={`font-medium ${settings.sendgrid_configured ? 'text-green-800' : 'text-amber-800'}`}>
              {settings.sendgrid_configured ? "Email Service Connected" : "Email Service Not Configured"}
            </h3>
            <p className={`text-sm ${settings.sendgrid_configured ? 'text-green-700' : 'text-amber-700'}`}>
              {settings.sendgrid_configured 
                ? "SendGrid is configured. Emails will be sent automatically."
                : "Add your SendGrid API key to enable email automation. Until then, notifications will be logged but not sent."}
            </p>
          </div>
        </div>
      </div>

      {/* Owner Email */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Owner Notification Email</h3>
        <div className="max-w-md">
          <Label>Email Address</Label>
          <Input
            type="email"
            placeholder="your@email.com"
            value={settings.owner_notification_email}
            onChange={(e) => setSettings(prev => ({ ...prev, owner_notification_email: e.target.value }))}
            className="mt-1"
          />
          <p className="text-sm text-gray-500 mt-1">New quote notifications will be sent to this email</p>
        </div>
      </div>

      {/* Notification Toggles */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Email Automations</h3>
        <div className="space-y-4">
          {notificationTypes.map(({ key, title, description, icon: Icon }) => (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{title}</h4>
                  <p className="text-sm text-gray-600">{description}</p>
                </div>
              </div>
              <Switch
                checked={settings[key]}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, [key]: checked }))}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary hover:bg-primary/90"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={handleTestEmail}
          disabled={testingEmail}
        >
          {testingEmail ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Send Test Email
            </>
          )}
        </Button>
      </div>

      {/* Help Text */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-2">Setting Up Email Automation</h4>
        <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
          <li>Sign up for a free SendGrid account at sendgrid.com</li>
          <li>Create an API key with full access</li>
          <li>Add the API key to your backend .env file as SENDGRID_API_KEY</li>
          <li>Restart the backend service to apply changes</li>
        </ol>
      </div>
    </div>
  );
};

export default NotificationSettings;
